import type {
  CreateCredentialDto,
  CredentialsApiDto,
} from '@terra/shared-contracts-applications';
import type {
  CredentialCookie,
  CredentialData,
} from './add-credentials-dialog/AddCredentialsDialogStore';

type Credential = CredentialsApiDto['credentials'][number];

const toDialogCookie = ({
  name,
  value,
}: {
  name: string;
  value: string;
}): CredentialCookie => ({
  name,
  value,
  path: null,
  domain: null,
  expires: null,
  httpOnly: null,
  secure: null,
  sameSite: null,
  priority: null,
});

export const mapCredentialToDialogData = (
  credential: Credential,
): CredentialData => ({
  id: credential.id,
  name: credential.name ?? null,
  ssoProvider: credential.ssoProvider,
  username: credential.username,
  role: credential.role,
  roleId: credential.roleId ?? null,
  roleDescription: credential.roleDescription ?? null,
  loginUrl: credential.loginUrl ?? null,
  password: credential.password ?? '',
  basicAuthUsername: credential.basicAuthUsername ?? '',
  basicAuthPassword: credential.basicAuthPassword ?? '',
  mtlsCert: credential.mtlsCert ?? '',
  mtlsKey: credential.mtlsKey ?? '',
  mtlsPfx: credential.mtlsPfx ?? '',
  mtlsOrigin: credential.mtlsOrigin ?? '',
  mtlsPassphrase: credential.mtlsPassphrase ?? '',
  tenant: credential.tenant ?? null,
  headers:
    credential.headers.length > 0
      ? credential.headers
      : [{ key: '', value: '' }],
  cookies:
    credential.cookies.length > 0
      ? credential.cookies.map(toDialogCookie)
      : [toDialogCookie({ name: '', value: '' })],
  secrets: credential.secrets,
  bodySignature: credential.bodySignature ?? null,
  mfaConfig: (credential.mfaConfig ?? []).map((mfa) => ({
    id: mfa.id,
    enabled: !!mfa.isActive,
    providerType: mfa.providerType,
    totpSecret: mfa.totpSecret ?? undefined,
  })),
  generatedMailboxAddress: credential.generatedMailboxAddress ?? '',
});

export const sanitizeCredential = (
  credentialData: CredentialData,
): CreateCredentialDto => ({
  ...credentialData,
  headers: credentialData.headers.filter(
    ({ key, value }) => key.trim() || value.trim(),
  ),
  cookies: credentialData.cookies.filter(
    ({ name, value }) => name.trim() || value.trim(),
  ),
  secrets: credentialData.secrets.filter(
    ({ key, value }) => key.trim() || value.trim(),
  ),
});
